import type {CreateReviewCommentMutationData} from '@github-ui/pull-requests/use-create-review-comment-mutation'
import {CommentSubjectType, type DiffOrientation, type DiffRange, type NewTreeEntry, type OldTreeEntry} from '../types'

export type ReviewCommentFactoryOpts = {
  filePath: string
  newTreeEntry?: NewTreeEntry | null | undefined
  oldTreeEntry?: OldTreeEntry | null | undefined
  effectiveDiffRange: DiffRange
  submitBatch?: boolean | undefined
  text: string
}

/**
 * Factory for building payloads used to create review comments.
 *
 * This class centralizes construction of CreateReviewCommentMutationData for
 * file-level comments, single-line comments, and multiline comments. It uses
 * the provided diff range and tree entry metadata to compute correct paths
 * and commit OIDs for the positioning information expected by the
 * useCreateReviewCommentMutation.
 */
export class ReviewCommentFactory {
  #baseCommitOid: string
  #filePath: string
  #headCommitOid: string
  #newTreeEntry: NewTreeEntry | null | undefined
  #oldTreeEntry: OldTreeEntry | null | undefined
  #submitBatch: boolean | undefined
  #text: string

  /**
   * Create a new ReviewCommentFactory.
   *
   * @param opts - Options for constructing request payloads
   * @param opts.filePath - The file path currently shown in the diff
   * @param opts.newTreeEntry - New tree entry metadata for the file (may be null/undefined)
   * @param opts.oldTreeEntry - Old tree entry metadata for the file (may be null/undefined)
   * @param opts.effectiveDiffRange - The diff range selection which supplies base and head commit OIDs
   * @param opts.submitBatch - Whether the comment should be submitted as part of a batch (pending review) or immediately
   * @param opts.text - The comment text to submit
   */
  constructor({filePath, newTreeEntry, oldTreeEntry, effectiveDiffRange, submitBatch, text}: ReviewCommentFactoryOpts) {
    this.#baseCommitOid = effectiveDiffRange.baseOid
    this.#filePath = filePath
    this.#headCommitOid = effectiveDiffRange.headOid
    this.#newTreeEntry = newTreeEntry
    this.#oldTreeEntry = oldTreeEntry
    this.#submitBatch = submitBatch
    this.#text = text
  }

  /**
   * Build request payload for a file-level review comment.
   *
   * The returned object conforms to CreateReviewCommentMutationData and does not set
   * positioning data. With positioning data set, the comment can't accurately be placed on a commit
   * if the file is either brand new or deleted. The file positioning info is not required to make a file level
   * comment, so it is omitted.
   */
  fileRequestData(): CreateReviewCommentMutationData {
    return {
      ...this.#commonRequestData(),
      path: this.#filePath,
      subjectType: CommentSubjectType.File,
    }
  }

  /**
   * Build request payload for a single-line review comment from individual parameters.
   *
   * @param params - Object containing line number and side
   * @param params.lineNumber - The line number for the comment
   * @param params.side - Whether the comment is on the 'left' or 'right' side
   * @returns CreateReviewCommentMutationData configured for a line-level comment
   */
  lineRequestData(params: {lineNumber: number; side: 'left' | 'right'}): CreateReviewCommentMutationData {
    const {lineNumber, side} = params

    return {
      ...this.#commonRequestData(),
      line: lineNumber,
      path: this.#path(side),
      positioning: {
        type: CommentSubjectType.Line,
        baseCommitOid: this.#baseCommitOid,
        headCommitOid: this.#headCommitOid,
        path: this.#path(side),
        line: lineNumber,
        commitOid: this.#commitOid(side),
      },
      side,
      subjectType: CommentSubjectType.Line,
    }
  }

  /**
   * Build request payload for a multiline review comment from individual parameters.
   *
   * @param params - Object containing line numbers and sides
   * @param params.startLineNumber - The starting line number
   * @param params.startSide - Whether the start is on the 'left' or 'right' side
   * @param params.endLineNumber - The ending line number
   * @param params.endSide - Whether the end is on the 'left' or 'right' side
   * @returns CreateReviewCommentMutationData configured for a multiline comment
   */
  multilineRequestData(params: {
    startLineNumber: number
    startSide: 'left' | 'right'
    endLineNumber: number
    endSide: 'left' | 'right'
  }): CreateReviewCommentMutationData {
    const {endLineNumber, endSide, startLineNumber, startSide} = params

    return {
      ...this.#commonRequestData(),
      line: endLineNumber,
      path: this.#path(endSide),
      positioning: {
        baseCommitOid: this.#baseCommitOid,
        headCommitOid: this.#headCommitOid,
        type: CommentSubjectType.Multiline,
        startPath: this.#path(startSide),
        startLine: startLineNumber,
        startCommitOid: this.#commitOid(startSide),
        endPath: this.#path(endSide),
        endLine: endLineNumber,
        endCommitOid: this.#commitOid(endSide),
      },
      side: endSide,
      startLine: startLineNumber,
      startSide,
      subjectType: CommentSubjectType.Multiline,
    }
  }

  #path(diffSide: DiffOrientation): string {
    if (
      diffSide === 'left' &&
      this.#oldTreeEntry?.path &&
      this.#newTreeEntry?.path &&
      this.#oldTreeEntry.path !== this.#newTreeEntry.path
    ) {
      return this.#oldTreeEntry.path
    }

    return this.#filePath
  }

  #commitOid(diffSide: DiffOrientation): string {
    if (diffSide === 'left') {
      return this.#baseCommitOid
    }

    return this.#headCommitOid
  }

  #commonRequestData(): Pick<
    CreateReviewCommentMutationData,
    'comparisonEndOid' | 'comparisonStartOid' | 'text' | 'submitBatch'
  > {
    return {
      comparisonEndOid: this.#headCommitOid,
      comparisonStartOid: this.#baseCommitOid,
      text: this.#text,
      submitBatch: this.#submitBatch,
    }
  }
}
